import React, {Fragment, useEffect} from "react"
import {iconCrown, iconGallery} from "../Helper/Icons"
import {MODAL__SOLUTION_REVIEW} from "../Hooks/Modal"
import {useCatalog, useSolution, useLocal, useModal, useRoomSetup, useLightbox} from "../AppContext"
import {useCart} from "../Hooks/Cart"
import {Button} from "../Components/Button"
import {Info} from "./Solution/Info"
import {LoadIndicator} from "../Components/LoadIndicator"
import {Modal} from "../Components/Modal"
import {InputSelect} from "../Components/Input"
import style from "./SolutionReview.css"

export const SolutionReview = ({setProduct}) => {
    const local = useLocal()
    const cart = useCart()
    const solution = useSolution()
    const roomSetup = useRoomSetup()

    const hasProducts = () => {
        return solution.hasPlannings() && solution.getItems().length > 0
    }

    useEffect(() => {
        if (!hasProducts()) {
            return
        }

        const action = async () => {
            try {
                solution.setCode(null)
                await roomSetup.save()
                await solution.setRoomId(roomSetup.getId())
                await solution.save()
                await cart.load(solution.getId(), local.getLang()).then()
                solution.handlerUnset()
                solution.updateVersion()
            } catch (error) {
                cart.setError(local.__(typeof error == "string" ? error : error.message))
            }
        }

        action().finally()
    }, [cart.shippingGet()])

    return <Modal type={MODAL__SOLUTION_REVIEW} userStyle={{width: "900px"}} bright={true}>
        <div className={style.layout}>
            <div>
                {cart.isQuote() && <Info title={local.__("Perfekt geplant! Jetzt sind wir dran!")} encouragement={local.__("Wow, das sieht gut aus! Ihre Auswahl enthält einige Absorber aus userer Premium Line. Diese Absorber benötigen genaues Augenmerk bei der Platzierung. Deshalb folgt nun unser Experten-Check, bei dem unsere Akustiker Ihre Planung <strong>kostenfrei überprüfen</strong> und Ihnen ein unverbitliches Angebot erstellen.")} bright={true}/>}
                {!cart.isQuote() && <Info title={local.__("Ihr Plan für gute Akustik steht!")} encouragement={local.__("Hier finden Sie noch einmal alle Details Ihrer Planung auf einen Blick. Mit einem Klick auf die Schaltfläche unten rechts werden Sie zu unserem aixFOAM Shop weitergeleitet und können Ihre individuelle Akustiklösung ganz bequem bestellen oder ein unverbindliches Angebot anfordern.")} bright={true}/>}
                {hasProducts() && <>
                    <Load cart={cart}/>
                    <Error cart={cart}/>
                    <Items cart={cart} setProduct={setProduct}/>
                </>}
                {!hasProducts() && <Empty/>}
            </div>
            <Footer cart={cart}/>
        </div>
    </Modal>
}

const Empty = () => {
    const local = useLocal()
    const modal = useModal()

    const openSolutionSuggestions = () => {
        modal.close()
        modal.openSolutionSuggestions()
    }

    return <div className={style.empty}>
        <div>
            <div>{local.__("Sie haben noch keine Produkte in Ihrem Raum platziert.")}</div>
            <div>{local.__("Lassen Sie sich von unseren Experten-Vorschlägen inspirieren.")}</div>
            <Button onClick={openSolutionSuggestions}>{local.__("Zu den Experten-Vorschlägen")}</Button>
        </div>
    </div>
}

const Load = ({cart}) => {
    return cart.isLoading() ? <div className={style.load}><LoadIndicator/></div> : null
}

const Error = ({cart}) => {
    return cart.hasError() ? <div className={style.error}>{cart.getError()}</div> : null
}

const Items = ({cart, setProduct}) => {
    return cart.getItems().map((item, index) => <Item key={index} item={item} setProduct={setProduct}/>)
}

const Item = ({item, setProduct}) => {
    const lightbox = useLightbox()
    const catalog = useCatalog()
    const local = useLocal()
    const image = item.image ?? catalog.getImage(item.sku)
    const sku = item.parentSku ?? catalog.getParentSku(item.sku) ?? item.sku
    const isPremium = item.price == 0
    const parentProduct = catalog.getParentProduct(sku)

    return <div className={style.item}>
        <div className={style.imageContainer} onClick={() => {setProduct(parentProduct); lightbox.openGallery();}}>
            <div className={style.image} style={{backgroundImage: `url('${image}')`}}/>
            <div className={style.galleryIcon}>{iconGallery}</div>
        </div>
        <div className={style.qty}>{item.qty}x</div>
        <div>
            <div className={style.name}>{item.name}</div>
            <div className={style.options}>
                <div>SKU:</div>
                <div>{sku}</div>
                {item.options.map((option, index) => <Fragment key={index}>
                    <div>{option.label}:</div>
                    <div>{option.value}</div>
                </Fragment>)}
            </div>
            {isPremium && <div className={style.premium}>{iconCrown} {local.__("Premium-Artikel. Angebot folgt nach Experten-Check.")}</div>}
        </div>
        <div className={style.price}>
            {!isPremium && <>
                <div>{local.currency(item.price)} </div>
                <div>{local.__("inkl. MwSt.")}</div>
            </>}
        </div>
    </div>
}

const Footer = ({cart}) => {
    return <div className={style.footer}>
        <FooterCart cart={cart}/>
        <FooterQuote cart={cart}/>
    </div>
}

const FooterCart = ({cart}) => {
    const local = useLocal()
    const modal = useModal()
    const total = local.currency(cart.getTotal())

    const close = () => {
        modal.close()
    }

    const gotoCart = () => {
        cart.gotoCart()
    }

    const changeShipping = (event) => {
        cart.shippingSet(event.target.value)
    }

    if (!cart.hasData() || cart.isQuote()) return null

    return <div className={style.cart}>
        <div className={style.close} onClick={close}>{local.__("zurück zur Planung")}</div>
        <div className={style.totals}>
            <div className={style.label}>{local.__("Lieferland:")}</div>
            <InputSelect value={cart.shippingGet()} options={cart.shippingGetOptions()} onChange={changeShipping} local={local}/>
            <div className={style.label}>{local.__("Gesammtsumme:")}</div>
            <div className={style.price}>
                <div>{total}</div>
                <div>{local.__("inkl. MwSt. und Versand")}</div>
            </div>
        </div>
        <Button className="review_to_cart" disabled={!cart.hasLink()} onClick={gotoCart}>{local.__("zum aixFOAM Warenkorb")}</Button>
    </div>
}

const FooterQuote = ({cart}) => {
    const local = useLocal()
    const modal = useModal()

    const close = () => {
        modal.close()
    }

    const gotoCart = () => {
        cart.gotoCart()
    }

    if (!cart.isQuote()) return null

    return <div className={style.quote}>
        <div className={style.close} onClick={close}>{local.__("zurück zur Planung")}</div>
        <Button className="review_to_quote" disabled={!cart.hasLink()} onClick={gotoCart}>{local.__("Anfrage stellen")}</Button>
    </div>
}